// The IconMenu component was removed from the new Material UI library, so in order
// to continue using an IconMenu component, we need to create our own if we want to
// fully use the new Material UI library version.
import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Menu } from '@mui';
import * as colors from '@constants/colors';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import './icon-menu.scss';

// React 17 has no useId() (that's React 18+) - a simple module-level counter gives each
// IconMenu instance its own stable id instead, for the aria-controls/id relationship below.
let nextMenuId = 0;
const getNextMenuId = () => {
  nextMenuId += 1;
  return nextMenuId;
};

const IconMenu = ({
  children, icon, iconStyles, iconButtonStyles, MenuProps, tooltip
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuId = useRef(`icon-menu-${getNextMenuId()}`).current;

  const handleClick = useCallback(event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, [setAnchorEl]);

  const handleClose = useCallback(event => {
    event.stopPropagation();
    setAnchorEl(null);
  }, [setAnchorEl]);

  return (
    <div styleName="icon-menu-container">
      <IconButton
        tooltip={tooltip}
        onClick={handleClick}
        style={iconButtonStyles}
        aria-label={tooltip || 'More actions'}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={menuId}
      >
        {icon}
        {!icon && <MoreVertIcon style={iconStyles} titleAccess="More actions" />}
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        {...MenuProps}
      >
        {children}
      </Menu>
    </div>
  );
};

IconMenu.propTypes = {
  MenuProps: PropTypes.object,
  /*
   * Contains the children nodes for the menu, and they can be like these
   *
   * import { Divider } from '@mui';
   *
   * ...
   * ...
   *
   * <IconMenu>
   *
   *   // The simplest menu item we can use:
   *   <IconMenuItem onClick={() => someFunc())}>Simple</IconMenuItem>
   *
   *   // A menu item with all features (icon and secondary text):
   *   <IconMenuItem onClick={() => someFunc())} icon={some icon} secondary={xxx}>All Features</IconMenuItem>
   *
   *   // We can also use a <Divider>:
   *   <Divider />
   *
   * </IconMenu>
   */
  children: PropTypes.node,
  icon: PropTypes.node,
  iconButtonStyles: PropTypes.object,
  iconStyles: PropTypes.object,
  tooltip: PropTypes.string
};

IconMenu.defaultProps = {
  iconButtonStyles: { padding: 0 },
  iconStyles: { color: colors.dotmapsBlack80 },
  MenuProps: {}
};

export default IconMenu;
